import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { adminOrdersList, type AdminOrderList } from "@motolynk/api-client";
import { formatDateTime, useUiLabels, type CustomerTabProps } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { DataTable, EmptyState, ErrorState, formatMoney, type ColumnDef } from "@motolynk/ui";

import { OrderStatusBadge } from "../shared/OrderParts";
import { adminOrderKeys } from "./source";

/** The admin customer page's "Orders" tab: the customer's last orders (opens the order). */
export function CustomerOrdersTab({
  customerUid,
  vendorUid,
}: CustomerTabProps & { vendorUid?: string }) {
  const { t, i18n } = useTranslation("orders");
  const ui = useUiLabels();
  const navigate = useNavigate();
  const query = {
    ...(vendorUid ? { vendor: vendorUid } : { customer: customerUid }),
    page_size: 50,
  };
  const rows = useQuery({
    queryKey: adminOrderKeys.list(query),
    queryFn: async () => (await adminOrdersList(query)).data,
  });
  if (rows.isError) {
    return (
      <ErrorState
        title={t("list.loadFailed")}
        retryLabel={ui.retry}
        onRetry={() => void rows.refetch()}
      />
    );
  }
  if (rows.data?.count === 0)
    return <EmptyState icon="inbox" title={t("admin.customerTab.empty")} />;
  const columns: ColumnDef<AdminOrderList>[] = [
    { id: "number", header: t("list.columns.order"), cell: ({ row }) => row.original.number },
    {
      id: "placed",
      header: t("admin.customerTab.placed"),
      cell: ({ row }) =>
        formatDateTime(row.original.placed_at ?? row.original.created_at, i18n.language),
    },
    {
      id: "total",
      header: t("list.columns.total"),
      cell: ({ row }) => formatMoney(row.original.grand_total, "INR", i18n.language),
    },
    {
      id: "status",
      header: t("list.columns.status"),
      cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
    },
  ];
  return (
    <DataTable
      columns={columns}
      data={rows.data?.results ?? []}
      total={rows.data?.count ?? 0}
      page={1}
      pageSize={50}
      onPageChange={() => undefined}
      loading={rows.isPending}
      getRowId={(row) => row.uid}
      onRowClick={(row) => void navigate(`/orders/${row.number}`)}
      labels={{ ...ui.table, emptyTitle: t("admin.customerTab.empty") }}
    />
  );
}
